import fetchWithAuth from "@/api/secureFetch";
import type { DashboardSort, Product, ProductContextMeshStatus, ProductListItemDto, ProductsListResponseDto } from "./types";
import {
    classifyContextMeshProvenance,
    ContextMeshApiError,
    fetchContextMeshAnalysis,
    type ContextMeshAnalysis,
    type ContextMeshAnalysisResponse
} from "../meshStrength/contextMeshAnalysis";

// Glyph color is uniform blue for now. When a stale/needs-update signal is
// wired up, swap this to orange for projects that have it.
const DEFAULT_GLYPH_COLOR = "#103699";

const formatUpdated = (iso: string | null): string => {
    if (!iso) return "—";
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const mapDtoToProduct = (dto: ProductListItemDto): Product => {
    const linkedContextMeshes = (dto.context_meshes ?? []).map(mesh => ({
        contextMeshId: mesh.context_mesh_id,
        name: mesh.name,
        linkRole: mesh.link_role,
        includeProductSources: mesh.include_product_sources,
        score: mesh.analysis_summary?.score ?? null,
        analysisStatus: mesh.analysis_summary?.status ?? "incomplete",
        updatedAt: mesh.updated_at
    }));
    const contextMeshScore = dto.context_mesh_score ?? null;
    const contextMeshStatus: ProductContextMeshStatus = dto.build?.state === "processing"
        ? "analyzing"
        : dto.build?.state === "failed" || dto.build?.state === "not_started"
          ? "unavailable"
          : contextMeshScore != null
            ? "ready"
            : "unavailable";

    return {
        id: dto.id,
        name: dto.name ?? "",
        sub: dto.sub ?? "",
        updated: formatUpdated(dto.updated),
        features: dto.features,
        stories: dto.stories,
        contextMeshAnalysis: null,
        contextMeshStatus,
        contextMeshScore: contextMeshScore ?? undefined,
        integrations: dto.integrations ?? [],
        owner: dto.owner ?? "",
        ownerName: dto.owner_name ?? "",
        color: DEFAULT_GLYPH_COLOR,
        cardState: dto.card_state ?? "standard",
        build: dto.build ?? null,
        linkedContextMeshes
    };
};

export interface ProductContextMeshResult {
    productId: string;
    analysis: ContextMeshAnalysis | null;
    status: Exclude<ProductContextMeshStatus, "loading">;
}

export interface ProductContextMeshRefreshResponse {
    productId: number;
    status: "scheduled";
    requestId: string;
}

const CONTEXT_MESH_CONCURRENCY = 4;

const classifyContextMeshAnalysis = (product: Product, response: ContextMeshAnalysisResponse): ProductContextMeshResult => {
    const productId = product.id;
    const classified = classifyContextMeshProvenance(response);
    if (classified.state === "current_canonical") {
        return { productId, analysis: classified.analysis, status: "ready" };
    }
    if (
        classified.state === "analyzing"
        || (classified.state === "prior_canonical" && response.semanticAnalysis.status === "pending")
    ) {
        return { productId, analysis: null, status: "analyzing" };
    }
    return { productId, analysis: null, status: "unavailable" };
};

/** Load canonical database-backed results without recreating score logic in Product Hub. */
export async function getProductContextMeshResults(token: string, products: Product[]): Promise<ProductContextMeshResult[]> {
    const results = new Array<ProductContextMeshResult>(products.length);
    let cursor = 0;
    async function worker(): Promise<void> {
        while (cursor < products.length) {
            const index = cursor++;
            const product = products[index];
            const productId = Number(product.id);
            const buildState = product.build?.state;
            if (buildState === "processing") {
                results[index] = { productId: product.id, analysis: null, status: "analyzing" };
                continue;
            }
            if (buildState === "failed" || buildState === "not_started") {
                results[index] = { productId: product.id, analysis: null, status: "unavailable" };
                continue;
            }
            if (!Number.isInteger(productId) || productId <= 0) {
                results[index] = { productId: product.id, analysis: null, status: "unavailable" };
                continue;
            }
            try {
                const analysis = await fetchContextMeshAnalysis(token, productId);
                results[index] = classifyContextMeshAnalysis(product, analysis);
            } catch (error) {
                results[index] = {
                    productId: product.id,
                    analysis: null,
                    status: error instanceof ContextMeshApiError && error.code === "BASELINE_INCOMPLETE" ? "baseline" : "unavailable"
                };
            }
        }
    }
    const workerCount = Math.min(CONTEXT_MESH_CONCURRENCY, products.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
}

/** Refresh one Product's canonical score, optionally promoting a preview
 * that was created against the source Product of a drastic rebuild. */
export async function requestProductContextMeshAnalysis(
    token: string,
    productId: string,
    options: { previewProductId?: number } = {}
): Promise<ProductContextMeshRefreshResponse> {
    const numericProductId = Number(productId);
    if (!Number.isInteger(numericProductId) || numericProductId <= 0) {
        throw new Error("This Product does not have a valid Context Mesh target.");
    }
    const response = await fetchWithAuth(`/api/products/${numericProductId}/context-analysis/refresh`, token, {
        method: "POST",
        headers: { Accept: "application/json" },
        ...(options.previewProductId != null
            ? { body: JSON.stringify({ previewProductId: options.previewProductId }) }
            : {})
    });
    let payload: unknown = null;
    try {
        payload = await response.json();
    } catch {
        // Use the safe generic message below when the server has no JSON body.
    }
    if (!response.ok) {
        const error = payload && typeof payload === "object" && "error" in payload
            ? (payload as { error?: { message?: unknown } }).error
            : null;
        throw new Error(
            error && typeof error.message === "string"
                ? error.message
                : "Context analysis could not be started. Try again shortly."
        );
    }
    return payload as ProductContextMeshRefreshResponse;
}

export const getProductsList = async (token: string, sort: DashboardSort = "updated"): Promise<Product[]> => {
    const response = await fetchWithAuth(`/api/user/projects/list?sort_by=${encodeURIComponent(sort)}`, token);
    if (!response.ok) {
        throw new Error(`Failed to load products (${response.status})`);
    }
    const body: ProductsListResponseDto = await response.json();
    return (body.projects ?? []).map(mapDtoToProduct);
};
